import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { UserContext } from "../shared/context/userContext";
import { getActiveFilters, isLincolnClientHandlingUser } from "./filterConfigs";
import { useOrderFilters } from "../shared/hooks/useOrderFilters";
import { DesktopFilters } from "./filterViews/DesktopFilters";
import { MobileFilters } from "./filterViews/MobileFilters";
import FiltersDrawer from "./components/FiltersDrawer";
import EMPFilterSidebar from "../shared/components/EMPFilterSidebar/EMPFilterSidebar";
import EMPButton from "../shared/components/EMPButton/EMPButton";
import MotionClose from "../shared/Icons/MotionClose";
import { isCanadianPreorderUser } from "../utils/canadianPreorderExperience";
import * as OrdersPageAnalytics from "../shared/services/analytics/OrdersPageAnalytics";

const OrdersFilters = ({
  isLoading,
  onFiltersApplied,
  isError,
  filterControls,
}) => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation("emp");
  const isUpdatedLincolnUser = isLincolnClientHandlingUser(user);
  const isCanadianPreorder = isCanadianPreorderUser(user);
  const isTablet = useMediaQuery(
    user?.brand === "L" ? "(max-width:767px)" : "(max-width:1024px)"
  );
  const activeFilters = getActiveFilters(
    user?.brand,
    user?.market,
    isCanadianPreorder,
    t,
    isUpdatedLincolnUser
  );
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);

  const localControls = useOrderFilters(
    filterControls ? null : onFiltersApplied,
    user
  );
  const { selectedFilters, handleFilterChange, handleClearFilters } =
    filterControls || localControls;

  const isDisabled = isLoading || isError;

  const handleOpenFilters = () => {
    setIsFiltersDrawerOpen(true);
  };

  const handleCloseFilters = () => {
    setIsFiltersDrawerOpen(false);
  };

  if (isCanadianPreorder) {
    const title = t("CanadianPreorderOrders.filters.title", {
      defaultValue: "Filters",
    });
    const filters = {
      actionRequired: {
        title: t("CanadianPreorderOrders.filters.actionRequired", {
          defaultValue: "Action Required",
        }),
        options: [
          {
            label: t("CanadianPreorderOrders.filters.actionRequiredOption", {
              defaultValue: "Show only action required",
            }),
            value: "yes",
          },
        ],
      },
      orderStatus: {
        ...activeFilters.orderStatus,
        title: t("CanadianPreorderOrders.filters.orderStatus", {
          defaultValue: "Order Status",
        }),
        infoTitle: t("CanadianPreorderOrders.filters.orderStatus", {
          defaultValue: "Order Status",
        }),
        infoText: t("CanadianPreorderOrders.filters.statusInfo", {
          defaultValue:
            "Created: Submitted but not actioned by the dealer. Accepted: Accepted by the dealer. Canceled: Canceled by the dealer or customer.",
        }),
        options: activeFilters.orderStatus.options.map((option) => ({
          ...option,
          label: t(`CanadianPreorderOrders.filters.${option.value}`, {
            defaultValue: option.label,
          }),
        })),
      },
    };
    const sidebar = (
      <EMPFilterSidebar
        filters={filters}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onFilterClear={handleClearFilters}
        disabled={isDisabled}
        hideClear={isUpdatedLincolnUser}
        clearLabel={t("CanadianPreorderOrders.filters.clear", {
          defaultValue: "Clear Filters",
        })}
        hideInactiveClear
      />
    );

    return isTablet ? (
      <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
        <EMPButton
          variant="outlined"
          disabled={isDisabled}
          onClick={() => {
            OrdersPageAnalytics.trackOpenFilters(user);
            handleOpenFilters();
          }}
        >
          {title}
        </EMPButton>
        <Drawer
          anchor="left"
          open={isFiltersDrawerOpen}
          onClose={handleCloseFilters}
          sx={{
            "& .MuiDrawer-paper": {
              width: "min(320px, 100vw)",
              boxSizing: "border-box",
              p: 4,
              overflowY: "auto",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Typography component="h2" variant="h5">
              {title}
            </Typography>
            <IconButton
              onClick={handleCloseFilters}
              aria-label={t("CanadianPreorderOrders.closeFilters", {
                defaultValue: "Close filters",
              })}
              data-testid="filters-drawer-close-button"
            >
              <MotionClose />
            </IconButton>
          </Box>
          {sidebar}
        </Drawer>
      </Box>
    ) : (
      <Box
        sx={{
          gridColumn: "1",
          minWidth: 0,
          alignSelf: "start",
          position: "sticky",
          top: "24px",
          "& .MuiFormControlLabel-root": { mr: 0 },
          "& .MuiFormControlLabel-label": { overflowWrap: "anywhere" },
          "& > .MuiBox-root": { bgcolor: "transparent", pl: 0 },
        }}
      >
        <Typography component="h2" variant="h5" sx={{ mb: 6 }}>
          {title}
        </Typography>
        {sidebar}
      </Box>
    );
  }

  return (
    <>
      {isTablet ? (
        <MobileFilters
          isDisabled={isDisabled}
          onOpenFilters={handleOpenFilters}
          user={user}
        />
      ) : (
        <DesktopFilters
          filters={activeFilters}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onFilterClear={handleClearFilters}
          hideClear={isUpdatedLincolnUser}
          isDisabled={isDisabled}
          isLoading={isLoading}
        />
      )}

      {/* Filters Drawer for mobile - always rendered but controlled by isTablet and state */}
      {isTablet && (
        <FiltersDrawer
          open={isFiltersDrawerOpen}
          onClose={handleCloseFilters}
          filters={activeFilters}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onFilterClear={handleClearFilters}
          hideClear={isUpdatedLincolnUser}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
};

OrdersFilters.propTypes = {
  filterControls: PropTypes.object,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  onFiltersApplied: PropTypes.func.isRequired,
};

OrdersFilters.defaultProps = {
  isError: false,
  isLoading: false,
};

export default OrdersFilters;
